import React, { useState, useEffect } from 'react';
import { BloodPressureRecord, RecordFormData, RecordValidationErrors, MeasurementArm } from '@/types/record';
import { validateBPValues, classifyBP } from '@/utils/bpClassification';
import { calculateMAP, calculatePulsePressure } from '@/utils/calculations';
import { getTodayDateString, getCurrentTimeString } from '@/utils/dateUtils';
import { Card } from '@/components/common/Card';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import { StampBadge } from '@/components/common/StampBadge';
import { RuledHeader } from '@/components/common/RuledHeader';

export interface RecordFormProps {
  onSubmit: (data: RecordFormData) => Promise<void> | void;
  onCancel?: () => void;
  initialRecord?: BloodPressureRecord | null;
  isModal?: boolean;
  isLoading?: boolean;
  title?: string;
  defaultDate?: string;
}

const QUICK_NOTES = [
  '晨起空腹',
  '睡前量測',
  '飯後測量',
  '服用降壓藥後',
  '運動後休息',
  '感到頭暈不適',
  '心情平靜',
  '工作勞累',
];

export const RecordForm: React.FC<RecordFormProps> = ({
  onSubmit,
  onCancel,
  initialRecord,
  isModal = false,
  isLoading = false,
  title,
  defaultDate,
}) => {
  const [systolic, setSystolic] = useState<number | ''>(
    initialRecord ? initialRecord.systolic : 120
  );
  const [diastolic, setDiastolic] = useState<number | ''>(
    initialRecord ? initialRecord.diastolic : 80
  );
  const [pulse, setPulse] = useState<number | ''>(
    initialRecord ? initialRecord.pulse : 72
  );
  const [measuredDate, setMeasuredDate] = useState<string>(
    initialRecord?.measured_date || defaultDate || getTodayDateString()
  );
  const [measuredTime, setMeasuredTime] = useState<string>(
    initialRecord?.measured_time || getCurrentTimeString()
  );
  const [arm, setArm] = useState<MeasurementArm>(
    initialRecord?.arm || 'left'
  );
  const [notes, setNotes] = useState<string>(
    initialRecord?.notes || ''
  );

  const [errors, setErrors] = useState<RecordValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Sync state if initialRecord changes
  useEffect(() => {
    if (initialRecord) {
      setSystolic(initialRecord.systolic);
      setDiastolic(initialRecord.diastolic);
      setPulse(initialRecord.pulse);
      setMeasuredDate(initialRecord.measured_date);
      setMeasuredTime(initialRecord.measured_time);
      setArm(initialRecord.arm || 'left');
      setNotes(initialRecord.notes || '');
    }
  }, [initialRecord]);

  // Live validation
  const validate = (): boolean => {
    const newErrors: RecordValidationErrors = {};

    const sysNum = Number(systolic);
    const diaNum = Number(diastolic);
    const pulseNum = Number(pulse);

    if (systolic === '' || isNaN(sysNum)) {
      newErrors.systolic = '請輸入收縮壓數值';
    } else if (sysNum < 40 || sysNum > 300) {
      newErrors.systolic = '收縮壓數值需介於 40 至 300 mmHg';
    }

    if (diastolic === '' || isNaN(diaNum)) {
      newErrors.diastolic = '請輸入舒張壓數值';
    } else if (diaNum < 30 || diaNum > 200) {
      newErrors.diastolic = '舒張壓數值需介於 30 至 200 mmHg';
    }

    if (pulse === '' || isNaN(pulseNum)) {
      newErrors.pulse = '請輸入心跳數值';
    } else if (pulseNum < 30 || pulseNum > 250) {
      newErrors.pulse = '心跳數值需介於 30 至 250 bpm';
    }

    if (
      systolic !== '' &&
      diastolic !== '' &&
      !isNaN(sysNum) &&
      !isNaN(diaNum) &&
      sysNum <= diaNum
    ) {
      newErrors.systolic = '收縮壓必須高於舒張壓';
    }

    if (!measuredDate) {
      newErrors.measuredDate = '請選擇測量日期';
    }

    if (!measuredTime) {
      newErrors.measuredTime = '請選擇測量時間';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Re-validate on change
  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      validate();
    }
  }, [systolic, diastolic, pulse, measuredDate, measuredTime]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({
      systolic: true,
      diastolic: true,
      pulse: true,
      measuredDate: true,
      measuredTime: true,
    });

    if (!validate()) return;

    await onSubmit({
      systolic: Number(systolic),
      diastolic: Number(diastolic),
      pulse: Number(pulse),
      measuredDate,
      measuredTime,
      arm,
      notes: notes.trim(),
    });
  };

  const handleAddQuickNote = (tag: string) => {
    if (!notes) {
      setNotes(tag);
    } else if (!notes.includes(tag)) {
      setNotes(`${notes}，${tag}`);
    }
  };

  // Live metrics
  const sysNum = Number(systolic);
  const diaNum = Number(diastolic);
  const isValidNumbers =
    systolic !== '' &&
    diastolic !== '' &&
    !isNaN(sysNum) &&
    !isNaN(diaNum) &&
    sysNum > diaNum;

  const currentCategory = isValidNumbers ? classifyBP(sysNum, diaNum) : null;
  const currentPP = isValidNumbers ? calculatePulsePressure(sysNum, diaNum) : null;
  const currentMAP = isValidNumbers ? calculateMAP(sysNum, diaNum) : null;

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Top Banner with live AHA status preview */}
      {isValidNumbers && currentCategory && (
        <div className="p-3.5 bg-paper-100 border border-paper-400 rounded-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-paper-sm">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-serif font-bold text-xs text-ink-900">
                即時臨床評估：
              </span>
              <StampBadge category={currentCategory.category} size="sm" />
            </div>
            <p className="font-serif text-[11px] text-ink-700">
              {currentCategory.recommendation}
            </p>
          </div>

          <div className="flex items-center gap-2 text-xs font-mono text-ink-600 bg-white/80 px-2.5 py-1 rounded-xs border border-paper-300 shrink-0">
            <span>脈壓: {currentPP} mmHg</span>
            <span className="text-paper-400">|</span>
            <span>MAP: {currentMAP} mmHg</span>
          </div>
        </div>
      )}

      {/* Main Measurements Grid (SYS, DIA, PULSE) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Systolic */}
        <Input
          label="收縮壓 (SYS)"
          unit="mmHg"
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          required
          stepper
          value={systolic}
          placeholder="120"
          error={touched.systolic ? errors.systolic : undefined}
          helperText="理想值 < 120"
          onChange={(e) => {
            setTouched((prev) => ({ ...prev, systolic: true }));
            setSystolic(e.target.value === '' ? '' : Number(e.target.value));
          }}
          onStepUp={() => {
            setTouched((prev) => ({ ...prev, systolic: true }));
            setSystolic((v) => (v === '' ? 120 : Math.min(Number(v) + 1, 300)));
          }}
          onStepDown={() => {
            setTouched((prev) => ({ ...prev, systolic: true }));
            setSystolic((v) => (v === '' ? 120 : Math.max(Number(v) - 1, 40)));
          }}
        />

        {/* Diastolic */}
        <Input
          label="舒張壓 (DIA)"
          unit="mmHg"
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          required
          stepper
          value={diastolic}
          placeholder="80"
          error={touched.diastolic ? errors.diastolic : undefined}
          helperText="理想值 < 80"
          onChange={(e) => {
            setTouched((prev) => ({ ...prev, diastolic: true }));
            setDiastolic(e.target.value === '' ? '' : Number(e.target.value));
          }}
          onStepUp={() => {
            setTouched((prev) => ({ ...prev, diastolic: true }));
            setDiastolic((v) => (v === '' ? 80 : Math.min(Number(v) + 1, 200)));
          }}
          onStepDown={() => {
            setTouched((prev) => ({ ...prev, diastolic: true }));
            setDiastolic((v) => (v === '' ? 80 : Math.max(Number(v) - 1, 30)));
          }}
        />

        {/* Pulse */}
        <Input
          label="心跳脈搏 (PULSE)"
          unit="bpm"
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          required
          stepper
          value={pulse}
          placeholder="72"
          error={touched.pulse ? errors.pulse : undefined}
          helperText="正常 60 ~ 100"
          onChange={(e) => {
            setTouched((prev) => ({ ...prev, pulse: true }));
            setPulse(e.target.value === '' ? '' : Number(e.target.value));
          }}
          onStepUp={() => {
            setTouched((prev) => ({ ...prev, pulse: true }));
            setPulse((v) => (v === '' ? 72 : Math.min(Number(v) + 1, 250)));
          }}
          onStepDown={() => {
            setTouched((prev) => ({ ...prev, pulse: true }));
            setPulse((v) => (v === '' ? 72 : Math.max(Number(v) - 1, 30)));
          }}
        />
      </div>

      {/* Date, Time & Arm Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
        {/* Date */}
        <div>
          <label className="block font-serif text-xs font-semibold uppercase tracking-wider text-ink-700 mb-1.5">
            測量日期 <span className="text-red-700">*</span>
          </label>
          <input
            type="date"
            required
            value={measuredDate}
            onChange={(e) => setMeasuredDate(e.target.value)}
            className="w-full bg-paper-100 border border-paper-400 rounded-xs px-3 py-2 text-ink-900 font-mono text-sm outline-none focus:bg-white focus:border-ink-900"
          />
        </div>

        {/* Time */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block font-serif text-xs font-semibold uppercase tracking-wider text-ink-700">
              測量時間 <span className="text-red-700">*</span>
            </label>
            <button
              type="button"
              onClick={() => setMeasuredTime(getCurrentTimeString())}
              className="text-[11px] font-serif text-ink-600 hover:text-ink-900 underline flex items-center gap-0.5"
            >
              <span>⏱ 現在</span>
            </button>
          </div>
          <input
            type="time"
            required
            value={measuredTime}
            onChange={(e) => setMeasuredTime(e.target.value)}
            className="w-full bg-paper-100 border border-paper-400 rounded-xs px-3 py-2 text-ink-900 font-mono text-sm outline-none focus:bg-white focus:border-ink-900"
          />
          {/* Quick Time Presets */}
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => {
                setMeasuredTime('07:30');
                handleAddQuickNote('晨起空腹');
              }}
              className="px-2 py-0.5 bg-paper-200 hover:bg-paper-300 border border-paper-400 text-ink-800 rounded-xs font-serif text-[11px] transition-colors"
            >
              🌅 晨起 (07:30)
            </button>
            <button
              type="button"
              onClick={() => {
                setMeasuredTime('13:00');
                handleAddQuickNote('午間');
              }}
              className="px-2 py-0.5 bg-paper-200 hover:bg-paper-300 border border-paper-400 text-ink-800 rounded-xs font-serif text-[11px] transition-colors"
            >
              🌞 午間 (13:00)
            </button>
            <button
              type="button"
              onClick={() => {
                setMeasuredTime('21:30');
                handleAddQuickNote('睡前');
              }}
              className="px-2 py-0.5 bg-paper-200 hover:bg-paper-300 border border-paper-400 text-ink-800 rounded-xs font-serif text-[11px] transition-colors"
            >
              🌙 睡前 (21:30)
            </button>
          </div>
        </div>

        {/* Measurement Arm */}
        <div>
          <label className="block font-serif text-xs font-semibold uppercase tracking-wider text-ink-700 mb-1.5">
            測量手臂部位
          </label>
          <div className="grid grid-cols-3 gap-1 h-[38px]">
            <button
              type="button"
              onClick={() => setArm('left')}
              className={`text-xs font-serif rounded-xs border transition-all ${
                arm === 'left'
                  ? 'bg-ink-900 text-paper-100 border-ink-900 font-bold'
                  : 'bg-paper-100 text-ink-700 border-paper-400 hover:bg-paper-200'
              }`}
            >
              左手
            </button>
            <button
              type="button"
              onClick={() => setArm('right')}
              className={`text-xs font-serif rounded-xs border transition-all ${
                arm === 'right'
                  ? 'bg-ink-900 text-paper-100 border-ink-900 font-bold'
                  : 'bg-paper-100 text-ink-700 border-paper-400 hover:bg-paper-200'
              }`}
            >
              右手
            </button>
            <button
              type="button"
              onClick={() => setArm('unspecified')}
              className={`text-xs font-serif rounded-xs border transition-all ${
                arm === 'unspecified'
                  ? 'bg-ink-900 text-paper-100 border-ink-900 font-bold'
                  : 'bg-paper-100 text-ink-700 border-paper-400 hover:bg-paper-200'
              }`}
            >
              未註記
            </button>
          </div>
        </div>
      </div>

      {/* Notes & Quick Tags */}
      <div className="space-y-2 pt-1">
        <div className="flex items-center justify-between">
          <label className="font-serif text-xs font-semibold uppercase tracking-wider text-ink-700">
            生理備註 / 狀態說明 (選填)
          </label>
          <span className="text-[11px] font-serif text-ink-400">
            如飲食、運動、服藥或身體感受
          </span>
        </div>

        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="例如：早起空腹量測、剛服降壓藥半小時、略有胸悶..."
          className="w-full bg-paper-100 border border-paper-400 rounded-xs px-3 py-2 text-ink-900 font-serif text-xs outline-none focus:bg-white focus:border-ink-900 placeholder:text-ink-400"
        />

        {/* Quick Tag Pills */}
        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
          <span className="font-serif text-[11px] text-ink-500 mr-1">快捷標籤:</span>
          {QUICK_NOTES.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => handleAddQuickNote(tag)}
              className="px-2 py-0.5 bg-paper-200 hover:bg-paper-300 border border-paper-400 text-ink-800 rounded-xs text-[11px] font-serif transition-colors"
            >
              + {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-3 border-t border-paper-300">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={onCancel}
            disabled={isLoading}
          >
            取消返回
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          size="md"
          isLoading={isLoading}
          className="min-w-[140px]"
        >
          {initialRecord ? '更新血壓紀錄' : '保存血壓紀錄'}
        </Button>
      </div>
    </form>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-950/60 backdrop-blur-xs animate-in fade-in duration-150">
        <div className="w-full max-w-2xl bg-white border-2 border-ink-900 rounded-xs shadow-paper-lg overflow-hidden">
          {/* Modal Header */}
          <div className="px-6 py-4 bg-paper-200 border-b border-paper-400 flex items-center justify-between">
            <div className="space-y-0.5">
              <h3 className="font-serif font-bold text-base uppercase tracking-wider text-ink-900">
                {title || (initialRecord ? '編輯臨床血壓紀錄' : '登記新血壓數據')}
              </h3>
              <p className="font-serif text-xs text-ink-500">
                AHA / WHO CLINICAL REGISTRATION FORM
              </p>
            </div>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="w-8 h-8 flex items-center justify-center text-ink-500 hover:text-ink-900 hover:bg-paper-300 rounded-xs font-mono text-lg transition-colors"
                aria-label="關閉"
              >
                ✕
              </button>
            )}
          </div>

          <div className="p-6">{formContent}</div>
        </div>
      </div>
    );
  }

  return (
    <Card
      title={title || (initialRecord ? '編輯臨床血壓紀錄' : '登記新血壓數據')}
      subtitle="CLINICAL BLOOD PRESSURE ENTRY FORM"
      showCrosshairs
    >
      {formContent}
    </Card>
  );
};
